/**
 * How the mission line is split on the map: remaining, travelled, and the
 * route we just left.
 *
 * `line-gradient` + `line-progress` stayed a single blue stroke in Mapbox —
 * the paint never updated after the first all-live expression, so the
 * travelled tail never went grey. The line is cut in geometry instead.
 */
export const TRAIL_LIVE = '#4c8bf5';
export const TRAIL_TRAVELED = '#7d868f';
export const TRAIL_GHOST = 'rgba(148, 156, 164, 0.38)';

const EARTH_M = 6371000;
/** How far the lead pin may sit off the centreline and still cut the trail. */
const LEAD_SNAP_M = 1200;

export function clampProgress(value: number | null | undefined, active: boolean): number {
  if (!active) return 0;
  if (value == null || !Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export function sameLine(
  a?: GeoJSON.LineString | null,
  b?: GeoJSON.LineString | null,
): boolean {
  const left = a?.coordinates || [];
  const right = b?.coordinates || [];
  if (left.length < 2 || right.length < 2) return false;
  const first = left[0];
  const last = left[left.length - 1];
  const otherFirst = right[0];
  const otherLast = right[right.length - 1];
  return first[0] === otherFirst[0]
    && first[1] === otherFirst[1]
    && last[0] === otherLast[0]
    && last[1] === otherLast[1]
    && left.length === right.length;
}

function meters(a: number[], b: number[]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

function lerp(a: number[], b: number[], t: number): GeoJSON.Position {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/** How far along the line a nearby point sits, or null when it is off the road. */
export function ratioAtPoint(
  coordinates: GeoJSON.Position[],
  point: GeoJSON.Position,
  snapM = LEAD_SNAP_M,
): number | null {
  if (coordinates.length < 2) return null;
  let bestDist = Infinity;
  let bestAlong = 0;
  let acc = 0;
  let total = 0;
  for (let i = 0; i < coordinates.length - 1; i += 1) {
    total += meters(coordinates[i], coordinates[i + 1]);
  }
  if (total <= 0) return null;
  for (let i = 0; i < coordinates.length - 1; i += 1) {
    const a = coordinates[i];
    const b = coordinates[i + 1];
    const span = meters(a, b);
    const ax = b[0] - a[0];
    const ay = b[1] - a[1];
    const denom = ax * ax + ay * ay;
    const t = denom <= 0 ? 0 : Math.min(1, Math.max(0, ((point[0] - a[0]) * ax + (point[1] - a[1]) * ay) / denom));
    const proj = lerp(a, b, t);
    const dist = meters(point, proj);
    if (dist < bestDist) {
      bestDist = dist;
      bestAlong = acc + span * t;
    }
    acc += span;
  }
  if (bestDist > snapM) return null;
  return Math.min(1, Math.max(0, bestAlong / total));
}

export function reportedProgress(
  progress?: { progress_ratio?: number | null; distance_along_m?: number | null; total_distance_m?: number | null } | null,
): number {
  if (!progress) return 0;
  const ratio = progress.progress_ratio;
  if (ratio != null && Number.isFinite(ratio) && ratio > 0) {
    return Math.min(1, Math.max(0, ratio));
  }
  const along = progress.distance_along_m;
  const total = progress.total_distance_m;
  if (along != null && total != null && total > 0 && along > 0) {
    return Math.min(1, Math.max(0, along / total));
  }
  return 0;
}

/** Lead pin used to grey the trail — any convoy fix, including off-route.
 *
 * Fixed collection sites are excluded outright: a tripod parked beside
 * kilometre 30 would grey the whole line up to it while the convoy is
 * still leaving the garage.
 */
export function pickTrailLead(
  tracks: Array<{ deviceId: string; longitude: number; latitude: number; isStationary?: boolean }>,
  leadId?: string | null,
): { longitude: number; latitude: number } | null {
  const moving = tracks.filter((t) => !t.isStationary);
  if (!moving.length) return null;
  const wanted = (leadId || '').toLowerCase();
  const named = wanted
    ? moving.find((t) => t.deviceId.toLowerCase() === wanted)
    : undefined;
  const track = named || moving[0];
  return { longitude: track.longitude, latitude: track.latitude };
}

export function trailCutRatio(
  geometry: GeoJSON.LineString,
  progress: number,
  lead?: { longitude: number; latitude: number } | null,
): number {
  const coords = geometry.coordinates || [];
  if (lead) {
    const fromLead = ratioAtPoint(coords, [lead.longitude, lead.latitude]);
    if (fromLead != null) return fromLead;
  }
  return Math.min(1, Math.max(0, Number(progress) || 0));
}

export function splitTrail(
  geometry: GeoJSON.LineString,
  progress: number,
): { traveled: GeoJSON.LineString | null; remaining: GeoJSON.LineString | null } {
  const coords = geometry.coordinates || [];
  if (coords.length < 2) return { traveled: null, remaining: null };
  const p = clampProgress(progress, true);
  if (p <= 0) return { traveled: null, remaining: geometry };
  if (p >= 1) return { traveled: geometry, remaining: null };

  let total = 0;
  const spans: number[] = [];
  for (let i = 0; i < coords.length - 1; i += 1) {
    const d = meters(coords[i], coords[i + 1]);
    spans.push(d);
    total += d;
  }
  if (total <= 0) return { traveled: null, remaining: geometry };

  const cut = total * p;
  let acc = 0;
  const traveled: GeoJSON.Position[] = [coords[0]];
  for (let i = 0; i < spans.length; i += 1) {
    const next = acc + spans[i];
    if (next < cut - 0.5) {
      traveled.push(coords[i + 1]);
      acc = next;
      continue;
    }
    const t = spans[i] > 0 ? (cut - acc) / spans[i] : 1;
    const mid = lerp(coords[i], coords[i + 1], Math.min(1, Math.max(0, t)));
    traveled.push(mid);
    const remaining: GeoJSON.Position[] = [mid, ...coords.slice(i + 1)];
    return {
      traveled: traveled.length >= 2 ? { type: 'LineString', coordinates: traveled } : null,
      remaining: remaining.length >= 2 ? { type: 'LineString', coordinates: remaining } : null,
    };
  }
  return { traveled: geometry, remaining: null };
}

export function ghostLines(
  current?: GeoJSON.LineString | null,
  ...candidates: Array<GeoJSON.LineString | null | undefined>
): GeoJSON.LineString[] {
  const out: GeoJSON.LineString[] = [];
  for (const line of candidates) {
    if (!line || (line.coordinates?.length || 0) < 2) continue;
    if (sameLine(line, current)) continue;
    if (out.some((kept) => sameLine(kept, line))) continue;
    out.push(line);
  }
  return out;
}
